import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import Button from '../../components/common/Button/Button';
import Input from '../../components/common/Input/Input';
import { useAuth } from '../../context/AuthContext';
import { getCustomerProfile, updateCustomerProfile } from '../../services/authApi';
import { updateNotificationPreferences } from '../../services/commerceApi';
import { useLanguage } from '../../context/LanguageContext';

function AccountPage() {
  const { accessToken, logout, setUser, user } = useAuth();
  const { translate } = useLanguage();
  const [values, setValues] = useState({ firstName: user?.firstName || '', lastName: user?.lastName || '' });
  const [errors, setErrors] = useState({});
  const [message, setMessage] = useState('');
  const [saving, setSaving] = useState(false);
  const [preferences, setPreferences] = useState(user?.notificationPreferences || { whatsappOptIn: false, smsOptIn: false, emailOptIn: true });

  useEffect(() => {
    getCustomerProfile(accessToken)
      .then((profile) => {
        setUser(profile);
        setValues({ firstName: profile.firstName, lastName: profile.lastName });
        setPreferences(profile.notificationPreferences || { whatsappOptIn: false, smsOptIn: false, emailOptIn: true });
      })
      .catch(() => setMessage('Unable to refresh your profile.'));
  }, [accessToken, setUser]);

  const change = (event) => setValues((current) => ({ ...current, [event.target.name]: event.target.value }));

  async function submit(event) {
    event.preventDefault();
    setErrors({});
    setMessage('');
    setSaving(true);
    try {
      const profile = await updateCustomerProfile(accessToken, values);
      setUser(profile);
      setMessage('Profile updated successfully.');
    } catch (error) {
      setErrors(error.details || {});
      setMessage(error.message);
    } finally {
      setSaving(false);
    }
  }

  async function savePreferences(event) {
    event.preventDefault(); setSaving(true); setMessage('');
    try { const profile = await updateNotificationPreferences(accessToken, preferences); setUser(profile); setMessage('Notification preferences updated successfully.'); }
    catch (error) { setMessage(error.message); }
    finally { setSaving(false); }
  }

  return (
    <section className="account-page">
      <div className="container">
        <div className="account-heading">
          <div><p className="eyebrow">{translate('Customer account')}</p><h1>{translate('Hello, {name}', { name: user?.firstName || '' })}</h1><p>{translate('Review your customer details and account security.')}</p></div>
          <Button onClick={logout} variant="secondary">{translate('Sign out')}</Button>
        </div>
        <div className="account-layout">
          <aside className="account-nav" aria-label={translate('Account navigation')}>
            <Link aria-current="page" to="/account">{translate('Profile')}</Link>
            <Link to="/account/addresses">{translate('Saved addresses')}</Link>
            <Link to="/account/orders">{translate('Order history')}</Link>
            <Link to="/account/security">{translate('Security & sessions')}</Link>
          </aside>
          <div className="account-panel">
            <div className="account-panel__heading">
              <div><h2>{translate('Personal details')}</h2><p>{translate('Your phone number and email are protected account identifiers.')}</p></div>
              <span className="status-badge status-badge--success">{translate('Protected account')}</span>
            </div>
            {message && <div className={`form-message ${message.includes('successfully') ? 'form-message--success' : 'form-message--error'}`} role="status">{message}</div>}
            <form onSubmit={submit}>
              <div className="auth-form__row">
                <Input autoComplete="given-name" error={errors.firstName} label={translate('First name')} name="firstName" onChange={change} required value={values.firstName} />
                <Input autoComplete="family-name" error={errors.lastName} label={translate('Last name')} name="lastName" onChange={change} required value={values.lastName} />
              </div>
              <Input disabled label={translate('Email address')} value={user?.email || ''} />
              <Input disabled label={translate('Mobile number')} value={user?.phone || ''} />
              <Button disabled={saving} type="submit">{saving ? translate('Saving…') : translate('Save changes')}</Button>
            </form>
            <form className="preference-form" onSubmit={savePreferences}>
              <h2>{translate('Notification preferences')}</h2>
              <p>{translate('Choose how you would like to receive optional account and order updates. Verification and security messages are generated only when required or requested.')}</p>
              <label><input checked={preferences.emailOptIn} onChange={(event) => setPreferences({ ...preferences, emailOptIn: event.target.checked })} type="checkbox" /> {translate('Email order and account updates')}</label>
              <label><input checked={preferences.smsOptIn} onChange={(event) => setPreferences({ ...preferences, smsOptIn: event.target.checked })} type="checkbox" /> {translate('SMS updates')}</label>
              <label><input checked={preferences.whatsappOptIn} onChange={(event) => setPreferences({ ...preferences, whatsappOptIn: event.target.checked })} type="checkbox" /> {translate('WhatsApp updates')}</label>
              <Button disabled={saving} type="submit">{translate('Save preferences')}</Button>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}

export default AccountPage;
